import { Flex, HStack, VStack } from "@chakra-ui/react";
import { Button, FAIcon, Link, PhoneInput, PhoneInputProps, Radio, RadioGroup, Typography } from "@sgi/gravity";
import { useCallback, useState } from "react";
import { useMfaSetupProxy } from "../Custom Hooks/useMfaSetupProxy";
import { faMessageLines, faPhone } from "@fortawesome/pro-solid-svg-icons";

export default function SetupMfa() {
  const [mfaOption, setMfaOption] = useState<string | boolean>('phone');

  const { continueAction } = useMfaSetupProxy({ mfaOption: mfaOption as 'phone' | 'email' | 'none' });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState('');
  const handlePhoneChange = useCallback<Required<PhoneInputProps>['onChange']>(e => {
    setPhoneNumber(e.value);
    setFormattedPhoneNumber(e.formattedValue);
  }, []);
  const phoneNumberError = '';

  const [method, setMethod] = useState("");
  const onChangeMethod = useCallback((value: string | boolean) => setMethod(value.toString()), []);

  const continueButtonDisabled = phoneNumber.length !== 10 || method === '';
  const handleContinueButtonClick = useCallback(() => {
    // at this point the proxy only ask for mfa type, so save phone number and method for later
    sessionStorage.setItem('authex-registration-signup-mfa-phone-number', phoneNumber);
    sessionStorage.setItem('authex-registration-signup-mfa-phone-method', method);
    setMfaOption('phone');
    setTimeout(() => continueAction.act(), 100);
  }, [continueAction, phoneNumber, method]);

  const handleSkipButtonClick = useCallback(() => {
    setMfaOption('none');
    setTimeout(() => continueAction.act(), 100);
  }, [continueAction]);

  return (
    <Flex direction="column" maxW={468} minH="100vh" justifyContent="center" mx="auto" data-testid="setup-mfa-page-test">
      <Typography variant="h2">
        Secure your account
      </Typography>
      <Typography variant="bodyBold">
        Secure your account with Multi-Factor Authentication.
      </Typography>
      <Typography variant="body" mb={8}>
        Each time you sign in to your MySGI account, you’ll need your password and a verification code.
        &nbsp;<Link href="https://www.mysgi.ca/help_pages/" isExternal={true}>Learn more</Link>
      </Typography>
      <PhoneInput
        autoFocus
        label="Your Phone Number"
        name="phone-input"
        onChange={handlePhoneChange}
        value={formattedPhoneNumber}
        isRequired
        error={phoneNumberError}
        data-testid="phone-number-input-test"
      />
      <Typography variant="bodySmall">
        Verification code will be sent to this number
      </Typography>
      <RadioGroup
        value={method}
        onChange={onChangeMethod}
        name="method"
        label="Send security code by"
        variant="card"
        isRequired
        controlStyleProps={{mt: 4}}
      >
        <HStack>
          <Radio value="text" flex={1} data-testid="text-me-radio-test">
            <VStack alignItems="center">
              <FAIcon icon={faMessageLines} />
              <span>Text Me</span>
            </VStack>
          </Radio>
          <Radio value="call" flex={1} data-testid="call-me-radio-test">
            <VStack alignItems="center">
              <FAIcon icon={faPhone} />
              <span>Call Me</span>
            </VStack>
          </Radio>
        </HStack>
      </RadioGroup>
      <Typography variant="body" my={4}>
        The number provided will only be used for account security, message and data rates may apply.
      </Typography>
      <Button
        isFullWidth
        data-testid="continue-button-test"
        disabled={continueButtonDisabled}
        onClick={handleContinueButtonClick}
        mt={2}
      >
        Continue
      </Button>
      <Button
        isFullWidth
        data-testid="skip-button-test"
        onClick={handleSkipButtonClick}
        variant="ghost"
        mt={2}
      >
        Skip for now
      </Button>
    </Flex>
  );
}